import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import { setFlag, SUBSCRIBED_FLAG } from "../../utils/preferences";
import { identify, subscribeToNewsletter } from "../../utils/klaviyo";
import TermsCheckbox from "../TermsCheckbox";

const MIDDLEWARE_URL = (process.env.REACT_APP_MIDDLEWARE_URL || "https://krunchmate-middleware.onrender.com").replace(/\/+$/, "");
const EMAIL_PATTERN = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]{2,}$/;

// localStorage can throw in private browsing or with site data blocked
const remember = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // the code is still shown on screen
  }
};

/**
 * NewsletterCapture — the email-gate form used in the site footer.
 * The middleware issues the 10% welcome code; Klaviyo adds the address to the newsletter list.
 */
export default function NewsletterCapture() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // 'ok' | 'invalid' | 'error' | null
  const [coupon, setCoupon] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim();
    if (!EMAIL_PATTERN.test(clean)) {
      setStatus("invalid");
      return;
    }
    if (!agreedToTerms) return;
    setLoading(true);
    try {
      const res = await fetch(`${MIDDLEWARE_URL}/api/lead-capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: clean, source: "footer" }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success === true) {
        const code = typeof data.coupon === "string" && /^[A-Z0-9-]{1,40}$/.test(data.coupon) ? data.coupon : "";
        setFlag(SUBSCRIBED_FLAG);
        remember("km_lead_email", clean);
        if (code) remember("km_welcome_coupon", code);
        identify(clean);
        subscribeToNewsletter(clean, "Footer signup form");
        setCoupon(code);
        setStatus("ok");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "ok") {
    return (
      <div className="w-full max-w-lg" role="status" data-testid="footer-newsletter-success">
        <p className="text-[color:var(--gold)] text-base">You're in. Welcome to the KrunchMate family! ✦</p>
        {coupon && (
          <p className="mt-3 text-cream/80 text-sm">
            Your 10% code: <span className="font-bold tracking-wider text-cream">{coupon}</span>. Use it at checkout.
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full max-w-lg" data-testid="footer-newsletter-form" noValidate>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          required
          maxLength={254}
          autoComplete="email"
          aria-label="Email address"
          placeholder="your@email.co.uk"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 bg-transparent border border-cream/25 rounded-full px-6 py-4 text-cream placeholder:text-cream/40 focus:outline-none focus:border-gold transition-colors font-body"
          data-testid="footer-email-input"
        />
        <button
          type="submit"
          disabled={loading || !agreedToTerms}
          className="btn-gold justify-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="footer-subscribe-btn"
        >
          {loading ? "Sending" : "Subscribe"} <ArrowRight size={16} />
        </button>
      </div>
      <TermsCheckbox id="footer-terms" checked={agreedToTerms} onChange={setAgreedToTerms} className="mt-4 text-cream" />
      {status === "invalid" && (
        <p className="mt-3 text-red-300 text-sm" role="alert" data-testid="footer-newsletter-error">
          Please enter a valid email address.
        </p>
      )}
      {status === "error" && (
        <p className="mt-3 text-red-300 text-sm" role="alert" data-testid="footer-newsletter-error">
          We couldn't sign you up right now. Please try again.
        </p>
      )}
      <p className="mt-3 text-cream/40 text-xs">
        By subscribing you agree to receive marketing emails from KrunchMate. Unsubscribe at any time. No spam, ever.
      </p>
    </form>
  );
}
